//dependencies
import { useAutoAnimate } from "@formkit/auto-animate/react";
import React, { useEffect } from "react";

//icons
import { faCircleXmark, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

// py-components
import {
	ArrayFieldState,
	CheckboxGroup,
	FormField,
	FormList,
	FormRadioButtons,
	FormSubscribe,
	InputNumeric,
	PageBanner,
} from "@performyard/py-components";
import { Option } from "@performyard/py-components/src/types/Option";

//components
import IconButton from "../../../../../../../components/Button/IconButton";
import { Result } from "../../../../../../../components/Inputs/SelectInput";
import TruncatedSelectInput from "../../../../../../../components/Inputs/SelectInput/TruncatedSelectInput";

// utils
import { hasAllowedCalculatorTypes } from "../../../utils";

//store
import { useEditFormStore } from "../../../useEditFormStore";
//internal
import { EditQuestionBlockProps } from "../../QuestionBlock";

const RadioOptions: Option<"sum" | "average">[] = [
	{
		value: "sum",
		label: "Sum",
	},
	{
		value: "average",
		label: "Average",
	},
];

const FormBuilderCalculatorNumericInput = ({
	value,
	onChange,
	placeholder,
}: {
	value: number | "";
	onChange: (value: number | "") => void;
	placeholder: string;
}) => {
	return (
		<InputNumeric
			value={value === "" ? null : value}
			onChange={(value) => onChange(value === null ? "" : value)}
			// resonable limit here since we're not dealing with enormous numbers
			precision={10}
			placeholder={placeholder}
			allowEmpty
			dangerouslySetClassName="bg-neutral-0"
		/>
	);
};

type CalculatorRowProps = {
	index: number;
	refId: string;
	weight?: number | "";
	onDelete: () => void;
};

const CalculatorRow = ({ index, refId, onDelete }: CalculatorRowProps) => {
	const question = useEditFormStore((state) => state.questions.find((question) => question.refId === refId));

	return (
		<div
			className="flex max-w-full min-w-0 flex-col gap-2 rounded-[4px] bg-neutral-1 p-2"
			data-testid="calculator-row"
		>
			<div className="flex items-center gap-2">
				<p className="text-paragraph-3 font-semibold">Question:</p>
				<p className="line-clamp-2 max-w-full grow overflow-hidden text-paragraph-3 font-regular text-wrap break-words">
					{question?.question}
				</p>
				<IconButton
					onClick={onDelete}
					icon={faCircleXmark}
					iconOnly
					testId="delete-row"
					aria-label="Delete row"
				/>
			</div>
			<div className="flex items-center gap-2">
				<FormField<number | "">
					id={`questions[${index}].weight`}
					label="Weight"
					labelPosition="left"
					input={(field) => {
						return (
							<FormBuilderCalculatorNumericInput
								value={field.value}
								onChange={field.onChange}
								placeholder="Numeric Value"
							/>
						);
					}}
				/>
			</div>
		</div>
	);
};

const CalculatorQuestionSelector = ({
	questionOptions,
	field,
}: {
	questionOptions: Result[];
	field: ArrayFieldState<{ refId: string; weight: number | "" }>;
}) => {
	const [ref] = useAutoAnimate();
	const { value } = field;

	useEffect(() => {
		//ensure all questions have an associated question
		const optionIds = questionOptions.map((option) => option.value);
		const questions = field?.value?.filter((question) => optionIds.includes(question?.refId)) || [];
		field.onChange(questions);
	}, [questionOptions.length]);

	const selector = (
		<div className={`${questionOptions.length ? "" : "opacity-50"}`}>
			<TruncatedSelectInput
				results={questionOptions}
				label="Choose which questions to add to your calculation"
				labelProps={{ size: "paragraph-3", weight: "semi-bold" }}
				placeholder="Make a selection"
				value={null}
				onChange={(value) => {
					// Update the form field
					field.pushValue({ refId: value.value, weight: 1 });
				}}
			/>
		</div>
	);

	if (!value || value.length === 0) {
		return selector;
	}

	return (
		<div>
			{selector}
			<div ref={ref} className="mt-4 flex max-w-full min-w-0 flex-col gap-2">
				{value.map((question, qIndex) => (
					<CalculatorRow
						key={`${question.refId}-${qIndex}`}
						index={qIndex}
						refId={question.refId}
						weight={question.weight}
						onDelete={() => {
							field.removeValue(qIndex);
						}}
					/>
				))}
			</div>
		</div>
	);
};

const CalculatorEdit = ({ index }: EditQuestionBlockProps) => {
	const { questionOptions } = useEditFormStore((state) => ({
		questionOptions: state.questions
			.filter((question) => {
				// Get the current question
				const currentQuestion = state.questions[index];
				if (question.refId === currentQuestion?.refId) {
					return false;
				}
				return hasAllowedCalculatorTypes(question);
			})
			.map((question) => ({
				value: question.refId,
				label: question.question,
			})),
	}));

	return (
		<FormSubscribe id="question-root" selector={(state) => [state.values]}>
			{() => (
				<div className="flex max-w-full min-w-0 flex-col gap-4">
					<FormRadioButtons<"sum" | "average">
						id="aggregation"
						label="Calculation Type"
						options={RadioOptions}
					/>
					<FormField<boolean>
						id="is_hidden"
						label="Calculation Visibility"
						input={(field) => (
							<CheckboxGroup
								id="is_hidden"
								value={[field.value || false]}
								options={[
									{
										value: true,
										label: "Hide calculation on review for all users",
									},
								]}
								onChange={(value) => field.onChange(value.length > 0)}
							/>
						)}
					/>
					<FormList<{ refId: string; weight: number | "" }>
						id="questions"
						input={(field) => (
							<CalculatorQuestionSelector questionOptions={questionOptions} field={field} />
						)}
					/>

					<FormSubscribe id="sub-questions" selector={(state) => [state.values.questions]}>
						{([questions]) => {
							if ((!questions || questions.length === 0) && !questionOptions.length) {
								return (
									<div>
										<PageBanner
											color="red"
											size="small"
											icon={faTriangleExclamation}
											mainTitle="Please add a question with rating scale or numeric answer to continue."
										/>
									</div>
								);
							}
							return null;
						}}
					</FormSubscribe>
				</div>
			)}
		</FormSubscribe>
	);
};

export default CalculatorEdit;
